import React, { useState, useRef } from 'react';
import CodeDisplay from '../CodeDisplay';
import Controls from '../Controls';
import VisualizationCanvas from '../VisualizationCanvasForGraph';
import AlgorithmDescription from './AlgorithmDescription';
import LoggerDrawer from '../LoggerDrawer';
import UpdateVariablesModal from './UpdateVariablesModal';
import LanguageDropdown from '../LanguageDropdown';
import defaultExplanations from './explanations';

import { useGraph } from './useGraph';

const Graph = ({ language, subTopicId }) => {
  const maxVertices = 6; // For C Languuage Only

  const [speakingLang, setSpeakingLang] = useState("en-US");
  const [translatedExplanations, setTranslatedExplanations] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [graphInput, setGraphInput] = useState([[10, 20], [10, 25], [20, 42], [25, 42], [25, 50]]);
  const [edgeDirection, setEdgeDirection] = useState([false, false, false, false, true]);
  const [firstEdges, setFirstEdges] = useState("10, 20");
  const [secondEdges, setSecondEdges] = useState("10, 25");
  const [thirdEdges, setThirdEdges] = useState("20, 42");
  const [fourthtEdges, setFourthtEdges] = useState("25, 42");
  const [fifthEdges, setFifthEdges] = useState("25, 50");

  const [inputEdgeDirection, setInputEdgeDirection] = useState(edgeDirection);
  const [inputFirstEdges, setInputFirstEdges] = useState(firstEdges);
  const [inputSecondEdges, setInputSecondEdges] = useState(secondEdges);
  const [inputThirdEdges, setInputThirdEdges] = useState(thirdEdges);
  const [inputFourthtEdges, setInputFourthtEdges] = useState(fourthtEdges);
  const [inputFifthEdges, setInputFifthEdges] = useState(fifthEdges);


  const [graphInputForC, setGraphInputForC] = useState([[1, 2], [1, 3], [2, 4], [3, 4], [3, 5]]);
  const [firstEdgesForC, setFirstEdgesForC] = useState("1, 2");
  const [secondEdgesForC, setSecondEdgesForC] = useState("1, 3");
  const [thirdEdgesForC, setThirdEdgesForC] = useState("2, 4");
  const [fourthtEdgesForC, setFourthtEdgesForC] = useState("3, 4");
  const [fifthEdgesForC, setFifthEdgesForC] = useState("3, 5");

  const [inputFirstEdgesForC, setInputFirstEdgesForC] = useState(firstEdgesForC);
  const [inputSecondEdgesForC, setInputSecondEdgesForC] = useState(secondEdgesForC);
  const [inputThirdEdgesForC, setInputThirdEdgesForC] = useState(thirdEdgesForC);
  const [inputFourthtEdgesForC, setInputFourthtEdgesForC] = useState(fourthtEdgesForC);
  const [inputFifthEdgesForC, setInputFifthEdgesForC] = useState(fifthEdgesForC);

  const edges = [inputFirstEdges, inputSecondEdges, inputThirdEdges, inputFourthtEdges, inputFifthEdges];
  const edgesForC = [inputFirstEdgesForC, inputSecondEdgesForC, inputThirdEdgesForC, inputFourthtEdgesForC, inputFifthEdgesForC];

  const [breakpoints, setBreakpoints] = useState([]);

  const editorCardRef = useRef(null);

  const handleChange = (e, index) => {
    const input = e.target.value;
    if (index === 0) {
      setInputFirstEdges(input);
    } else if (index === 1) {
      setInputSecondEdges(input);
    } else if (index === 2) {
      setInputThirdEdges(input);
    } else if (index === 3) {
      setInputFourthtEdges(input);
    } else if (index === 4) {
      setInputFifthEdges(input);
    }
  };

  const handleChangeForC = (e, index) => {
    const input = e.target.value;

    if (index === 0) {
      setInputFirstEdgesForC(input);
    } else if (index === 1) {
      setInputSecondEdgesForC(input);
    } else if (index === 2) {
      setInputThirdEdgesForC(input);
    } else if (index === 3) {
      setInputFourthtEdgesForC(input);
    } else if (index === 4) {
      setInputFifthEdgesForC(input);
    }
  };
  

  const {
    highlightedLine,
    highlightedMultipleLines,
    focusedEndLine,
    callingLine,
    secondCallingLine,
    thirdCallingLine,
    isRunning,
    isPaused,
    delay,
    logs,
    graphVariables,
    highlightedGraphVariables,
    variables,
    adjList,
    highlightedVariables,
    mapVariables,
    highlightedMapVariables,
    highlightedMapIndex,
    mapVariablesForC,
    highlightedMapVariablesForC,
    highlightedMapIndexForC,
    highlightedLineArrow,
    nodeVariables,
    highlightNodeAddress,
    highlightedNodeVariables,
    highlightDataPart,
    highlightNextAddressPart,
    isMuted,
    codeSamples,
    setDelay,
    setIsPaused,
    setIsMuted,
    updateVariables,
    runGraph
  } = useGraph(speakingLang, translatedExplanations, language, graphInput, edgeDirection, graphInputForC, maxVertices, breakpoints);


  const sanitizeInput = (input) => {
    const parts = input.split(",").map((item) => parseInt(item.trim()) || 0);
    return parts.length === 2 ? parts : [parts[0] || 0, parts[1] || 0];
  };

  const sanitizeInputForC = (input) => {
    const parts = input.split(",").map((item) => {
      const num = parseInt(item.trim()) || 0;
      // Ensure the number is between 1 and 5; otherwise, default to 0
      return num >= 1 && num <= 5 ? num : 0;
    });
    return parts.length === 2 ? parts : [parts[0] || 0, parts[1] || 0];
  };
  

  const handleUpdateVariables = () => {
    if (language === "C") {
      const updatedGraphInputForC = [
        sanitizeInputForC(inputFirstEdgesForC),
        sanitizeInputForC(inputSecondEdgesForC),
        sanitizeInputForC(inputThirdEdgesForC),
        sanitizeInputForC(inputFourthtEdgesForC),
        sanitizeInputForC(inputFifthEdgesForC),
      ];
  
      setGraphInputForC(updatedGraphInputForC);
  
      setEdgeDirection(inputEdgeDirection);
  
      setFirstEdgesForC(sanitizeInputForC(inputFirstEdgesForC).join(", "));
      setSecondEdgesForC(sanitizeInputForC(inputSecondEdgesForC).join(", "));
      setThirdEdgesForC(sanitizeInputForC(inputThirdEdgesForC).join(", "));
      setFourthtEdgesForC(sanitizeInputForC(inputFourthtEdgesForC).join(", "));
      setFifthEdgesForC(sanitizeInputForC(inputFifthEdgesForC).join(", "));
      
      updateVariables(updatedGraphInputForC, inputEdgeDirection);
      setModalVisible(false);
    } else {
      const updatedGraphInput = [
        sanitizeInput(inputFirstEdges),
        sanitizeInput(inputSecondEdges),
        sanitizeInput(inputThirdEdges),
        sanitizeInput(inputFourthtEdges),
        sanitizeInput(inputFifthEdges),
      ];
  
      setGraphInput(updatedGraphInput);
  
      setEdgeDirection(inputEdgeDirection);
  
      setFirstEdges(sanitizeInput(inputFirstEdges).join(", "));
      setSecondEdges(sanitizeInput(inputSecondEdges).join(", "));
      setThirdEdges(sanitizeInput(inputThirdEdges).join(", "));
      setFourthtEdges(sanitizeInput(inputFourthtEdges).join(", "));
      setFifthEdges(sanitizeInput(inputFifthEdges).join(", "));
      
      updateVariables(updatedGraphInput, inputEdgeDirection);
      setModalVisible(false);
    }
  };

  return (
    <div className="container-fluid main-container">
      <div className="content-area d-flex">
        <div className="col-lg-6" ref={editorCardRef}>
          <CodeDisplay
            codeLines={codeSamples || []}
            highlightedLine={highlightedLine}
            highlightedMultipleLines={highlightedMultipleLines}
            focusedEndLine={focusedEndLine}
            topic={"Graph"}
            callingLine={callingLine}
            secondCallingLine={secondCallingLine}
            thirdCallingLine={thirdCallingLine}
            subTopicId={subTopicId}
            language={language}
            isRunning={isRunning}
            breakpoints={breakpoints}
            setBreakpoints={setBreakpoints}
            onOpenModal={() => {
              setInputEdgeDirection(edgeDirection);

              setInputFirstEdges(firstEdges);
              setInputSecondEdges(secondEdges);
              setInputThirdEdges(thirdEdges);
              setInputFourthtEdges(fourthtEdges);
              setInputFifthEdges(fifthEdges);

              setInputFirstEdgesForC(firstEdgesForC);
              setInputSecondEdgesForC(secondEdgesForC);
              setInputThirdEdgesForC(thirdEdgesForC);
              setInputFourthtEdgesForC(fourthtEdgesForC);
              setInputFifthEdgesForC(fifthEdgesForC);

              setModalVisible(true);
            }}
          />
        </div>
        <div className="col-lg-6">
          <div className="visualize-card shadow-lg p-3">
            <div className="d-flex justify-content-center position-relative">
              <h3 className="section-title text-center flex-grow-1">Canvas</h3>
              <LanguageDropdown
                onLanguageChange={setSpeakingLang}
                onTranslate={setTranslatedExplanations}
                selectedProgrammingLang={language}
                defaultExplanations={defaultExplanations}
                isRunning={isRunning} 
                setIsTranslating={setIsTranslating} 
              />
            </div>

            <Controls
              isRunning={isRunning}
              isPaused={isPaused}
              delay={delay}
              setDelay={setDelay}
              runVisualization={runGraph}
              setIsPaused={setIsPaused}
              isMuted={isMuted}
              setIsMuted={setIsMuted}
              isTranslating={isTranslating}
              editorCardRef={editorCardRef}
            />
            <VisualizationCanvas
              graphVariables={graphVariables}
              highlightedGraphVariables={highlightedGraphVariables}
              variables={variables}
              highlightedVariables={highlightedVariables}
              adjList={adjList}
              mapVariables={mapVariables}
              highlightedMapVariables={highlightedMapVariables}
              highlightedMapIndex={highlightedMapIndex}
              mapVariablesForC={mapVariablesForC}
              highlightedMapVariablesForC={highlightedMapVariablesForC}
              highlightedMapIndexForC={highlightedMapIndexForC}
              highlightedLineArrow={highlightedLineArrow}
              nodeVariables={nodeVariables}
              highlightNodeAddress={highlightNodeAddress}
              highlightedNodeVariables={highlightedNodeVariables}
              highlightDataPart={highlightDataPart}
              highlightNextAddressPart={highlightNextAddressPart}
            />
            <LoggerDrawer logs={logs} />
          </div>
        </div>
      </div>
      <div className="col-lg-12 mt-4">
          <AlgorithmDescription />
      </div>
      <UpdateVariablesModal
        isVisible={modalVisible}
        language={language}
        edgeDirection={inputEdgeDirection}
        edges={edges}
        edgesForC={edgesForC}
        handleChange={handleChange}
        handleChangeForC={handleChangeForC}
        setEdgeDirection={setInputEdgeDirection}
        handleSave={handleUpdateVariables}
        handleClose={() => setModalVisible(false)}
      />
    </div>
  );
};

export default Graph;
